<template>
  <div :class="$style.container">
    <h1 :class="$style.header">
      Duo 2 Factor Authentication
    </h1>

    <Alert
      :class="[
        $style.alertPositionWidth,
        $style.alertPosition
      ]"
      v-model="errorMessage"
    />

    <transition-group
      :class="[
        $style.frameContainerColor,
        $style.frameContainerPosition,
        $style.frameContainerSize,
        $style.frameContainerWidth,
      ]"
      :enterClass="$style.fadeTo"
      :enterActiveClass="$style.fadeInTransition"
      :leaveActiveClass="$style.fadeOutTransition"
      :leaveToClass="$style.fadeTo"
      tag="div"
    >
      <i
        v-show="!showFrame"
        key="loading"
        :class="['jc-progress', 'jc-spin', $style.spinAnimation, $style.loadingIcon]"
      />
      <iframe
        v-show="showFrame"
        key="frame"
        :class="$style.frame"
        id="duo_iframe"
      />
    </transition-group>
    <form
      id="duo_form"
      method="POST"
    >
      <input
        v-if="isSso"
        name="context"
        type="hidden"
        value="sso"
      >
      <input
        v-if="isSso"
        name="redirect_to"
        type="hidden"
        :value="redirectTo"
      >
    </form>

    <ChooseMfa
      v-bind="$props"
      :selectedFactor="factor"
    />
  </div>
</template>
<script>
/* eslint camelcase: 0 */
// Types
import { Duo } from 'client/containers/Login/MfaType';

// Constants
import { LoginConsts } from 'client/util/Constants';

// Router
import RouteGenerator, { UserRoutes } from 'client/routes/RouteGenerator';

// API
import api from 'client/api';

// Utils
import loginResponseHandlers from 'client/containers/Login/LoginResponseHandlers';
import loginUtil from 'client/components/mixins/loginUtil';

// Components
import ChooseMfa from 'client/containers/Login/ChooseMfa.vue';

const { login } = RouteGenerator;

export default {
  name: 'LoginDuo',

  components: {
    ChooseMfa,
  },

  mixins: [
    loginResponseHandlers,
    loginUtil,
  ],

  props: {
    factor: {
      type: Duo,
      required: true,
    },
    factors: {
      type: Array,
      default: () => ([]),
    },
    context: {
      type: String,
      default: '',
    },
    redirectTo: {
      type: String,
      default: '',
    },
    challenge: {
      type: Object,
      default: null,
    },
    passwordResetInfo: {
      type: Object,
      default: null,
    },
    available: {
      type: Boolean,
      default: true,
    },
  },

  computed: {
    isSso() {
      return (this.context === LoginConsts.sso);
    },
  },

  data() {
    return {
      errorMessage: '',
      showFrame: false,
      ssoConst: LoginConsts.sso,
      token: '',
    };
  },

  duoScriptLoader: () => new Promise((resolve, reject) => {
    const script = document.createElement('script');

    script.onload = () => { resolve(); };
    script.onerror = () => { reject(); };
    script.async = true;
    script.src = 'https://api.duosecurity.com/frame/hosted/Duo-Web-v2.min.js';

    document.head.appendChild(script);
  }),

  mounted() {
    if (this.passwordResetInfo) {
      this.passwordReset();
      return;
    }

    this.duoSetup();
  },

  methods: {
    handlePasswordResetError(error) {
      // See LoginResponseHandlers
      this.mfaErrorRedirectHandler(this.factor.name, error, this.passwordResetInfo)();
    },

    passwordReset() {
      const { setErrorMessage } = this;

      return this.$options.duoScriptLoader()
        .then(() => this.handleScriptLoaded(this.challenge))
        .catch(() => setErrorMessage('Unable to load Duo Web JavaScript file!'));
    },

    duoSetup() {
      const { handleScriptLoaded, setErrorMessage } = this;

      const parseDuoAuth = (response) => {
        if (response && response.data) {
          return this.$options.duoScriptLoader()
            .then(() => handleScriptLoaded(response.data))
            .catch(() => setErrorMessage('Unable to load Duo Web JavaScript file!'));
        }

        return setErrorMessage('Unable to retrieve Duo configuration!');
      };

      if (!this.available) {
        return this.setErrorMessage("This account isn't registered in Duo. A registered Duo account is required for Duo MFA. See your IT administrator for help.");
      }

      return api[this.clientType]
        .getDuoAuth()
        .then((response) => parseDuoAuth(response))
        .catch(() => setErrorMessage('Unable to retrieve Duo configuration!'));
    },

    handleScriptLoaded(duoInfo) {
      const { api_host, sig_request, token } = duoInfo;
      this.duoInit(api_host, sig_request);
      this.token = token;
      this.showFrame = true;
    },

    /**
     * This method is provided for testing because stubbing methods or properties of
     * the global window object isn't possible.
     */
    duoInit(host, sig_request) {
      if (window && window.Duo) {
        const params = { host, sig_request };
        if (this.passwordResetInfo) {
          params.submit_callback = this.duoCallback;
        } else {
          params.submit_callback = this.duoVerify;
        }
        window.Duo.init(params);
      }
    },

    /**
     * This method sends an XHR to /auth/duo
     * to verify the token and signature
     */
    duoVerify(form) {
      const sig_resp = form.elements.sig_response.value;
      const form_token = this.token;

      return api[this.clientType].postDuoSignature(
        { sig_response: sig_resp, token: form_token },
      )
        .then(() => {
          if (this.context === LoginConsts.sso) {
            const { ssoRedirectUrl } = UserRoutes;
            this.redirect(ssoRedirectUrl(this.redirectTo));
          } else {
            this.redirect(login);
          }
        })
        .catch(() => this.setErrorMessage('Unable to verify Duo. If this problem persists, contact your IT Admin.'));
    },

    duoCallback(form) {
      // eslint-disable-next-line vue/no-mutating-props
      this.passwordResetInfo.sig_response = form.elements.sig_response.value;
      // eslint-disable-next-line vue/no-mutating-props
      this.passwordResetInfo.token = this.token;

      return api[this.clientType].resetPassword(
        this.passwordResetInfo.confirmationCode,
        this.passwordResetInfo,
      )
        .then((res) => {
          const { email, totpKey } = res.data;
          const redirectUrl = totpKey ? UserRoutes.setupMfa(totpKey, email) : login;
          this.redirect(redirectUrl);
        })
        .catch(this.handlePasswordResetError);
    },
  },
};
</script>
<style module>
@import '../css/login-components.css';

@value (jcGrey300) from 'css/brand-colors.css';
@value (fadeTransition, fadeTransitionDelayed) from 'css/transitions.css';
@value (max-width-medium, max-width-large) from 'css/breakpoints.css';

.container {
  text-align: unset;
}

.header {
  composes: header from '../css/login-components.css';
  margin-top: 110px;
}

.frameContainerColor {
  background-color: white;
}

.frameContainerSize {
  border-radius: 10px;
  height: 50vh;
  margin: 2rem auto 2.5rem;
}

.frameContainerPosition {
  align-items: center;
  display: flex;
  justify-content: center;
  overflow: hidden;
  position: relative;
}

.alertPositionWidth,
.frameContainerWidth {
  width: 90vw;
}

.alertPosition {
  margin-left: auto;
  margin-right: auto;
}

@media (min-width: max-width-medium) {
  .alertPositionWidth,
  .frameContainerWidth {
    width: 75vw;
  }
}

@media (min-width: max-width-large) {
  .alertPositionWidth,
  .frameContainerWidth {
    width: 50vw;
  }
}

.frame {
  border: 0 none;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
}

.fadeTo {
  opacity: 0;
}

.fadeInTransition {
  transition: fadeTransitionDelayed;
}

.fadeOutTransition {
  transition: fadeTransition;
}

.loadingIcon {
  color: jcGrey300;
  display: inline-block;
  font-size: 2rem;
}

/*
 * TODO: Move these styles, and the above markup, to a loadingIcon component
 * which could also use an SVG instead of the silly icon font.
 */
.spinAnimation {
  animation: spin 2s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
